import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Bell,
    CalendarDays,
    CheckCircle2,
    ClipboardList,
    FileText,
    FolderOpen,
    ParkingSquare,
    Shield,
    TrendingUp,
    Users,
    Landmark,
    HeartHandshake,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

function StatTile({ label, value, sublabel, testId }) {
    return (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-2" data-testid={`${testId}-value`}>
                {value}
            </div>
            {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
        </div>
    );
}

function ActionCard({ to, icon: Icon, title, body, meta, tone = "default", testId }) {
    const toneClass = {
        default: "border-slate-200 bg-white hover:bg-slate-50",
        urgent: "border-rose-200 bg-rose-50 hover:bg-rose-100/60",
        warning: "border-amber-200 bg-amber-50 hover:bg-amber-100/60",
        success: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100/60",
    }[tone] || "border-slate-200 bg-white hover:bg-slate-50";

    return (
        <Link
            to={to}
            data-testid={testId}
            className={`block border rounded-lg p-5 transition-colors duration-150 ${toneClass}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Icon size={18} className="text-slate-700" />
                    <h3 className="font-heading text-base font-semibold text-slate-900">{title}</h3>
                </div>
                {meta && (
                    <span className="shrink-0 text-xs font-semibold text-slate-700 bg-white/80 border border-slate-200 rounded px-2 py-1">
                        {meta}
                    </span>
                )}
            </div>
            <p className="text-sm text-slate-600 mt-2">{body}</p>
        </Link>
    );
}

function WorkItem({ item, emptyText, testId }) {
    if (!item) {
        return (
            <div className="border border-slate-200 rounded-lg bg-white p-4 text-sm text-slate-500" data-testid={testId}>
                {emptyText}
            </div>
        );
    }
    return (
        <Link
            to={item.href || "/cases"}
            className="block border border-slate-200 rounded-lg bg-white p-4 hover:bg-slate-50"
            data-testid={testId}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-heading text-sm font-semibold text-slate-900">{item.title}</div>
                    {item.body && <div className="text-sm text-slate-500 mt-1">{item.body}</div>}
                </div>
                {item.badge && (
                    <span className="shrink-0 text-[11px] uppercase tracking-wide border border-slate-200 rounded px-2 py-0.5 text-slate-600">
                        {item.badge}
                    </span>
                )}
            </div>
        </Link>
    );
}

const isOpenCase = (item) => !["resolved", "closed"].includes(item.status);
const formatDate = (value) => value ? new Date(value).toLocaleDateString() : "";

export default function Dashboard() {
    const { user } = useAuth();
    const [cases, setCases] = useState([]);
    const [docs, setDocs] = useState([]);
    const [chargePeriods, setChargePeriods] = useState([]);
    const [property, setProperty] = useState(null);
    const [directorDigest, setDirectorDigest] = useState(null);
    const [digest, setDigest] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [calendar, setCalendar] = useState([]);
    const [triage, setTriage] = useState(null);
    const [privacyRequests, setPrivacyRequests] = useState([]);
    const [retention, setRetention] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const safeGet = async (url, config) => {
                try {
                    const res = await api.get(url, config);
                    return res.data;
                } catch {
                    return null;
                }
            };

            try {
                const now = new Date();
                const in45 = new Date(now);
                in45.setDate(now.getDate() + 45);
                const reqs = [
                    safeGet("/cases"),
                    safeGet("/documents"),
                    safeGet("/notifications"),
                    safeGet("/calendar", {
                        params: {
                            start: now.toISOString().slice(0, 10),
                            end: in45.toISOString().slice(0, 10),
                        },
                    }),
                    user?.role === "agency_viewer"
                        ? Promise.resolve(null)
                        : safeGet("/routines/digest"),
                ];
                if (user?.role === "estate_admin" || user?.role === "agency_viewer") {
                    reqs.push(safeGet("/agency/digest"));
                } else {
                    reqs.push(Promise.resolve(null));
                }
                if (user?.role === "estate_admin") {
                    reqs.push(
                        safeGet("/admin/triage"),
                        safeGet("/admin/privacy-requests", { params: { status: "pending" } }),
                        safeGet("/admin/retention-reviews"),
                        safeGet("/admin/deployment-readiness"),
                    );
                }

                const [c, d, n, cal, routinesDg, dg, tr, pr, rr, rd] = await Promise.all(reqs);
                setCases(c || []);
                setDocs(d || []);
                setNotifications(n || []);
                setCalendar(cal || []);
                setDirectorDigest(routinesDg || null);
                setDigest(dg || null);
                setTriage(tr || null);
                setPrivacyRequests(pr || []);
                setRetention(rr || null);
                setReadiness(rd || null);
                if (user?.property_id) {
                    const p = await safeGet("/properties");
                    setProperty((p || []).find((pp) => pp.id === user.property_id));
                }
                // Separate, independent fetch — deliberately not folded into the
                // fixed-position `reqs` array above, so adding it can't shift every
                // other destructured variable by one.
                if (["resident", "household_admin", "estate_admin"].includes(user?.role)) {
                    const periods = await safeGet("/finance/charge-periods");
                    setChargePeriods(periods || []);
                }
            } catch {
                // The dashboard should degrade gracefully if one operational panel is unavailable.
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    const open = cases.filter((c) => c.status === "open").length;
    const total = cases.length;
    const privateDocs = docs.filter((d) => d.scope === "private").length;
    const estateKb = docs.filter((d) => d.scope === "estate_kb").length;
    const unread = notifications.filter((n) => !n.is_read).length;
    const urgentUnread = notifications.filter((n) => !n.is_read && n.severity === "urgent").length;
    const openParking = cases.filter((c) => c.category === "parking" && isOpenCase(c)).length;
    const missingChecklist = cases.filter((c) => (c.evidence_checklist || []).some((i) => ["missing", "needs_confirmation"].includes(i.status))).length;
    const upcoming = calendar
        .filter((event) => event.href)
        .slice(0, 3)
        .map((event) => ({
            title: event.title,
            body: `${formatDate(event.date)}${event.description ? ` - ${event.description}` : ""}`,
            href: event.href,
            badge: event.event_type?.replaceAll("_", " "),
        }));
    const nextNotification = notifications.find((n) => !n.is_read) || notifications[0];
    const adminSummary = triage?.summary || {};
    const readinessBlocked = readiness?.items?.filter((item) => item.status === "missing").length || 0;
    const retentionDue = retention?.summary?.total || 0;
    const pendingPrivacy = privacyRequests.length;
    const staleOpen = digest?.stale_open_cases || adminSummary.stale || 0;
    const agencyActions = digest?.agency_action_items?.length || 0;
    // First-run: a member with nothing on file yet. Give them one obvious path (the money
    // journey) instead of a grid of empty "0" cards. Additive — the full grid still renders.
    const firstRun =
        !loading &&
        ["resident", "household_admin", "estate_admin"].includes(user?.role) &&
        total === 0 &&
        docs.length === 0 &&
        chargePeriods.length === 0 &&
        notifications.length === 0;

    const primaryActions = [];
    if (user?.role !== "agency_viewer") {
        primaryActions.push({
            to: "/parking",
            icon: ParkingSquare,
            title: "Challenge a parking ticket",
            body: "Use estate rules, title-deed evidence, and precedent examples to build a draft appeal.",
            meta: openParking ? `${openParking} active` : "Start",
            tone: openParking ? "warning" : "default",
            testId: "quick-link-parking",
        });
        primaryActions.push({
            to: missingChecklist ? "/cases" : "/documents",
            icon: FileText,
            title: "Complete evidence",
            body: "Upload permits, title-deed extracts, photos, or PCN letters without exposing private material by default.",
            meta: missingChecklist ? `${missingChecklist} needs review` : `${privateDocs} private docs`,
            tone: missingChecklist ? "warning" : "default",
            testId: "quick-link-evidence",
        });
    }
    primaryActions.push({
        to: "/cases",
        icon: FolderOpen,
        title: user?.role === "agency_viewer" ? "View shared cases" : "Manage cases",
        body: user?.role === "agency_viewer"
            ? "Review estate issues that residents or admins have deliberately shared with the agency."
            : "Track maintenance, safety, parking, and communal-area issues with resident-controlled visibility.",
        meta: `${open} open`,
        tone: open ? "default" : "success",
        testId: "quick-link-cases",
    });
    primaryActions.push({
        to: "/notifications",
        icon: Bell,
        title: "Review reminders",
        body: "See case responses, deadlines, retention reviews, and household sharing approvals in one inbox.",
        meta: unread ? `${unread} unread` : "Clear",
        tone: urgentUnread ? "urgent" : unread ? "warning" : "success",
        testId: "quick-link-notifications",
    });
    primaryActions.push({
        to: "/calendar",
        icon: CalendarDays,
        title: "Open estate calendar",
        body: "Check due dates, chase dates, resident deadlines, and upcoming retention reviews.",
        meta: `${calendar.length} events`,
        testId: "quick-link-calendar",
    });
    if (["resident", "household_admin", "estate_admin"].includes(user?.role)) {
        primaryActions.unshift({
            to: "/life",
            icon: HeartHandshake,
            title: "Council tax, emergencies, local help",
            body: "Official pages for bills, parking, communal issues, and support. We do not invent your band or amount.",
            testId: "quick-link-life",
        });
        primaryActions.unshift(
            chargePeriods.length === 0
                ? {
                      to: "/money",
                      icon: Landmark,
                      title: "Start with your service charge statement",
                      body: "Add this year's charge period to get a line-by-line breakdown with every figure traced to its source.",
                      meta: "Get started",
                      testId: "quick-link-money",
                  }
                : {
                      to: "/money",
                      icon: Landmark,
                      title: "Where your money went",
                      body: `See ${chargePeriods[0].label}'s line items, the split across units, and which figures a director has confirmed.`,
                      testId: "quick-link-money",
                  }
        );
    }
    if (["resident", "household_admin", "estate_admin"].includes(user?.role)) {
        primaryActions.push({
            to: "/privacy",
            icon: Shield,
            title: "Control my data",
            body: "Export your records, request correction or deletion, and audit when your data was shared or accessed.",
            meta: "GDPR",
            testId: "quick-link-privacy",
        });
    }
    if (user?.role === "estate_admin") {
        primaryActions.unshift({
            to: "/admin/triage",
            icon: ClipboardList,
            title: "Run management triage",
            body: "Assign cases, set due dates, chase the agency, record management responses, and escalate repeated issues.",
            meta: adminSummary.due_overdue ? `${adminSummary.due_overdue} overdue` : `${adminSummary.total || 0} cases`,
            tone: adminSummary.due_overdue ? "urgent" : adminSummary.chase_due ? "warning" : "default",
            testId: "quick-link-triage",
        });
        primaryActions.push({
            to: "/admin/privacy",
            icon: Shield,
            title: "Privacy and deployment controls",
            body: "Review GDPR requests, retention queues, notification delivery, digest schedules, and production blockers.",
            meta: readinessBlocked ? `${readinessBlocked} blocker${readinessBlocked === 1 ? "" : "s"}` : `${retentionDue} reviews`,
            tone: readinessBlocked ? "urgent" : pendingPrivacy || retentionDue ? "warning" : "success",
            testId: "quick-link-admin-privacy",
        });
        primaryActions.push({
            to: "/admin",
            icon: Users,
            title: "Administer estate register",
            body: "Invite residents, manage households, import parking spaces, and keep property mappings accurate.",
            meta: "Register",
            testId: "quick-link-admin",
        });
    }
    if (user?.role === "estate_admin" || user?.role === "agency_viewer") {
        primaryActions.push({
            to: "/agency",
            icon: TrendingUp,
            title: "Agency digest",
            body: "View shared estate issues, repeated PCN patterns, stale cases, and required agency actions.",
            meta: digest ? `${agencyActions} actions` : "Digest",
            tone: agencyActions || staleOpen ? "warning" : "default",
            testId: "quick-link-agency",
        });
    }

    const managementItem = user?.role === "estate_admin" ? {
        title: adminSummary.due_overdue
            ? "Overdue management cases need attention"
            : adminSummary.chase_due
                ? "Agency or contractor chases are due"
                : "Management queue is under control",
        body: `${adminSummary.open || 0} open, ${adminSummary.stale || 0} stale, ${adminSummary.safety_open || 0} safety cases.`,
        href: "/admin/triage",
        badge: adminSummary.due_overdue ? "urgent" : adminSummary.chase_due ? "chase" : "triage",
    } : null;
    const agencyItem = digest ? {
        title: digest.management_summary || "Agency digest ready",
        body: `${digest.agency_shared_open_cases || 0} agency-shared open cases, ${digest.reported_parking_cases || 0} reported parking cases.`,
        href: "/agency",
        badge: staleOpen ? `${staleOpen} stale` : "digest",
    } : null;
    const privacyItem = user?.role === "estate_admin" ? {
        title: pendingPrivacy || retentionDue || readinessBlocked ? "Privacy operations need review" : "Privacy operations are current",
        body: `${pendingPrivacy} pending resident requests, ${retentionDue} retention reviews, ${readinessBlocked} deployment blockers.`,
        href: "/admin/privacy",
        badge: readinessBlocked ? "blocked" : pendingPrivacy ? "requests" : "privacy",
    } : null;

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <header>
                <div className="text-xs uppercase tracking-widest text-slate-500">Welcome back</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="dashboard-heading">
                    {user?.full_name}
                </h1>
                {property && (
                    <div className="text-sm text-slate-500 mt-1" data-testid="dashboard-property">
                        {property.unit_number} - {property.address}
                    </div>
                )}
            </header>

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile label="Open cases" value={open} sublabel={`of ${total} total`} testId="stat-open-cases" />
                <StatTile label="Unread" value={unread} sublabel={urgentUnread ? `${urgentUnread} urgent` : "Notifications"} testId="stat-unread" />
                {user?.role === "estate_admin" ? (
                    <StatTile label="Triage due" value={(adminSummary.due_overdue || 0) + (adminSummary.due_today || 0)} sublabel={`${adminSummary.chase_due || 0} chases due`} testId="stat-triage-due" />
                ) : user?.role === "agency_viewer" ? (
                    <StatTile label="Agency-shared" value={digest?.agency_shared_open_cases || 0} sublabel="Open cases" testId="stat-agency-shared" />
                ) : (
                    <StatTile label="My documents" value={privateDocs} sublabel="Private uploads" testId="stat-private-docs" />
                )}
                {digest ? (
                    <StatTile label="Estate actions" value={agencyActions} sublabel={`${digest.total_cases || 0} total cases`} testId="stat-estate-actions" />
                ) : (
                    <StatTile label="Estate KB" value={estateKb} sublabel="Rules & references" testId="stat-estate-kb" />
                )}
            </section>

            {firstRun && (
                <section
                    className="rounded-lg border border-slate-200 bg-white p-6"
                    data-testid="dashboard-start-here"
                >
                    <div className="flex items-center gap-2">
                        <Landmark size={18} className="text-slate-700" />
                        <h2 className="font-heading text-xl font-bold text-slate-900">Start here</h2>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                        The quickest way to see where your estate's money goes. You can stop after
                        any step — nothing is lost.
                    </p>
                    <ol className="mt-4 space-y-2 text-sm text-slate-700 max-w-2xl">
                        <li><span className="font-semibold">1.</span> Upload your service charge statement or budget.</li>
                        <li><span className="font-semibold">2.</span> Check each figure against the document it came from.</li>
                        <li><span className="font-semibold">3.</span> See what's missing from the agent's pack.</li>
                    </ol>
                    <Link
                        to="/money"
                        data-testid="start-here-money"
                        className="inline-block mt-5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm px-5 h-10 leading-10"
                    >
                        Upload your service charge statement
                    </Link>
                </section>
            )}

            <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 className="font-heading text-xl font-bold text-slate-900">Next actions</h2>
                    {loading && <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard...</div>}
                </div>
                {directorDigest?.items?.length > 0 && (
                    <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="director-digest">
                        <h3 className="font-heading text-sm font-semibold text-slate-900">Computed reminders</h3>
                        <p className="text-xs text-slate-500 mt-1">{directorDigest.disclaimer}</p>
                        <ul className="mt-3 space-y-2">
                            {directorDigest.items.slice(0, 6).map((item) => (
                                <li key={item.key}>
                                    <Link to={item.href || "/"} className="text-sm underline underline-offset-2">
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dashboard-action-grid">
                    {primaryActions.map((action) => (
                        <ActionCard key={`${action.to}-${action.title}`} {...action} />
                    ))}
                </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="space-y-3" data-testid="dashboard-operations-panel">
                    <div className="flex items-center gap-2">
                        <ClipboardList size={18} className="text-slate-700" />
                        <h2 className="font-heading text-lg font-bold text-slate-900">Operations</h2>
                    </div>
                    <WorkItem
                        item={managementItem || agencyItem || {
                            title: open ? "Your open cases are ready to manage" : "No open cases",
                            body: open ? "Review status, evidence, visibility, and any management responses." : "Create a case when a parking, communal, safety, or maintenance issue appears.",
                            href: "/cases",
                            badge: open ? "cases" : "clear",
                        }}
                        emptyText="No operations work right now."
                        testId="dashboard-primary-work"
                    />
                    {privacyItem && (
                        <WorkItem
                            item={privacyItem}
                            emptyText="No privacy work right now."
                            testId="dashboard-privacy-work"
                        />
                    )}
                </div>

                <div className="space-y-3" data-testid="dashboard-calendar-panel">
                    <div className="flex items-center gap-2">
                        <CalendarDays size={18} className="text-slate-700" />
                        <h2 className="font-heading text-lg font-bold text-slate-900">Upcoming</h2>
                    </div>
                    {upcoming.length === 0 ? (
                        <WorkItem emptyText="No upcoming due dates or chase dates in the next 45 days." testId="dashboard-upcoming-empty" />
                    ) : (
                        upcoming.map((item, index) => (
                            <WorkItem key={`${item.href}-${index}`} item={item} testId={`dashboard-upcoming-${index}`} />
                        ))
                    )}
                </div>

                <div className="space-y-3" data-testid="dashboard-notifications-panel">
                    <div className="flex items-center gap-2">
                        {urgentUnread ? <AlertTriangle size={18} className="text-rose-700" /> : <CheckCircle2 size={18} className="text-slate-700" />}
                        <h2 className="font-heading text-lg font-bold text-slate-900">Latest reminder</h2>
                    </div>
                    <WorkItem
                        item={nextNotification ? {
                            title: nextNotification.title,
                            body: nextNotification.body,
                            href: nextNotification.href || "/notifications",
                            badge: nextNotification.severity,
                        } : null}
                        emptyText="No reminders or approvals need attention."
                        testId="dashboard-latest-notification"
                    />
                </div>
            </section>
        </div>
    );
}
